import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

import {
  Bodytext,
  Button,
  Card,
  Heading,
  Spinner,
} from '@acorns/web-components'
import { useAnalyticsClient } from '@acorns/web-utils'
import styled from 'styled-components'

import CheckBadge from 'assets/icons/check-badge.png'
import ErrorIcon from 'assets/icons/errorIcon-purple.png'
import LinkBadge from 'assets/icons/link-badge.png'
import ConfirmationModal, {
  ModalActionTypes,
} from 'components/ConfirmationModal'
import FullscreenTakeover from 'components/FullscreenTakeover'
import useWindowDimensions from 'hooks/useWindowDimensions'
import { style } from 'theme'

import { SUPPORT_URL } from '../../../../constants'
import { CloseAccountEdgeCaseActionStripWrapper } from './action-strip-wrapper'
import {
  NegativeBalanceContactSupport,
  NegativeBalancePageViewedFields,
  NoLinkedAccount,
  NoLinkedAccountButtonTapped,
  PendingInvestmentsPageViewedFields,
  PendingInvestmentsViewHistory,
} from './segment'

type Props = {
  eligibilityStatus: {
    eligible: boolean
    status: string
    recurringOn?: boolean
    account?: string
    tier?: string
  }
  isDowngradeFlow?: boolean
  handleBackPress: () => void
  isWebView: boolean
  showRecurringTransferModal: boolean
  setShowRecurringTransferModal: (showRecurringTransferModal: boolean) => void
  handleRecurringTransferCancellation: () => void
  setShowEdgeCaseView: (showEdgeCaseView: boolean) => void
}

const Loader = styled.div`
  align-items: center;
  display: flex;
  height: 100vh;
`

const StyledCard = styled(Card)<{ innerHeight?: number }>`
  overflow: scroll;
  box-shadow: 0px 3px 15px 2px rgba(0, 0, 0, 0.02);
  max-width: 800px;
  min-height: 352px;
  margin: 0 auto;
  padding: 50px 0;
  text-align: center;
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    margin-top: 0;
    height: ${(props) => `calc(${props.innerHeight}px - 78px)`};
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 50px 30px;
    align-items: center;
    justify-content: center;
    border-radius: 0 !important;
  }
`

const StyledWVCard = styled(Card)<{ innerHeight?: number }>`
  overflow: scroll;
  text-align: center;
  margin-top: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
  justify-content: center;
  border-radius: 0 !important;
  padding: 0 30px 75px 30px;
  height: ${(props) => `calc(${props.innerHeight}px - 80px)`};
  &&& {
    border-radius: 0;
  }
`

const StyledBody = styled(Bodytext)`
  max-width: 420px;
  display: block;
  margin: 10px auto !important;
  letter-spacing: 0px;
  line-height: 22px;
  text-align: center;
  padding-bottom: 20px;
`
const StyledHeading = styled(Heading)`
  font-size: 22px !important;
  font-weight: 600;
  line-height: 30px;
`
const StyledCloseAccountButton = styled(Button)`
  margin-top: 30px !important;
  width: 240px;
  margin: 0 auto;
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    margin-top: auto !important;
    width: 100%;
  }
`

const RedStyledCloseAccountButton = styled(StyledCloseAccountButton)`
  background-color: ${style((theme) => theme.colors.red)} !important;
  margin-top: 30px;
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    margin-top: 20px;
  }
`

const StyledImage = styled.img`
  height: 104px;
  width: 104px;
  border-radius: 100px;
  margin-bottom: 30px;
  background-color: ${style((theme) => theme.colors.ivory)};
  padding: 26px;
`
const StyledLinkImage = styled.img`
  height: 104px;
  width: 104px;
  border-radius: 92px;
  margin: 27px;
`

const CenteredContainer = styled.div`
  margin: auto;
`

/**
 * When customer is not able to begin downgrade/closure flows due to product specific blockers, they are shown this component that communicates the blocking issue, and (if available) provides action items for customer to act upon.
 *
 * Blocking data is provided by the closure eligibility check utility in src/pages/settings/my-subscription/tier-subscription-center/utils.ts
 */

const Page: React.FC<Props> = ({
  isWebView,
  handleBackPress,
  eligibilityStatus,
  isDowngradeFlow,
  setShowRecurringTransferModal,
  handleRecurringTransferCancellation,
  showRecurringTransferModal,
  setShowEdgeCaseView,
}) => {
  const CardComponent = isWebView ? StyledWVCard : StyledCard
  const history = useHistory()
  const analytics = useAnalyticsClient()

  const { height } = useWindowDimensions()

  const [showRecurringTransfersOn, setShowRecurringTransfersOn] =
    useState(false)
  const [showPendingTransfers, setShowPendingTransfers] = useState(false)
  const [showNegativeBalance, setShowNegativeBalance] = useState(false)
  const [showNoLinkedAccount, setShowNoLinkedAccount] = useState(false)
  const [loading, setLoading] = useState(true)

  const handleCloseRecurringModal = () => {
    setShowEdgeCaseView(false)
    setShowRecurringTransferModal(false)
    setShowRecurringTransfersOn(false)
  }
  const handleViewHistory = () => {
    if (isWebView) {
      analytics.track(
        'Button Tapped',
        PendingInvestmentsViewHistory,
        {},
        () => {
          window.location.assign('acorns://exit?navigate_to=present')
        },
      )
    } else {
      analytics.track(
        'Button Tapped',
        PendingInvestmentsViewHistory,
        {},
        () => {
          history.push('/present')
        },
      )
    }
  }
  const handleLinkAccount = () => {
    analytics.track('Button Tapped', NoLinkedAccountButtonTapped, {}, () => {
      if (isWebView) {
        window.location.assign('acorns://exit?navigate_to=link-funding-source')
      } else {
        history.push('/settings/linked-accounts')
      }
    })
  }
  const handleContactSupport = () => {
    analytics.track('Button Tapped', NegativeBalanceContactSupport, {}, () => {
      if (isWebView) {
        window.location.assign('acorns://exit?navigate_to=contact-support')
      } else {
        window.location.assign(SUPPORT_URL)
      }
    })
  }

  useEffect(() => {
    if (
      eligibilityStatus?.status === 'pending investments' ||
      eligibilityStatus?.status === 'pending transactions'
    ) {
      if (eligibilityStatus.recurringOn) {
        setShowRecurringTransfersOn(true)
      }
      analytics.track(
        'Screen Viewed',
        PendingInvestmentsPageViewedFields(eligibilityStatus?.tier ?? ''),
      )
      setShowPendingTransfers(true)
    } else if (eligibilityStatus?.status === 'negative balance') {
      analytics.track(
        'Screen Viewed',
        NegativeBalancePageViewedFields(eligibilityStatus?.tier ?? ''),
      )
      setShowNegativeBalance(true)
    } else if (eligibilityStatus?.status === 'link account') {
      analytics.track('Screen Viewed', NoLinkedAccount)
      setShowNoLinkedAccount(true)
    }

    return setLoading(false)
  }, [])

  return (
    <>
      {loading && (
        <Loader>
          <Spinner />
        </Loader>
      )}
      {!loading && isWebView && (
        <FullscreenTakeover innerHeight={height}>
          <CloseAccountEdgeCaseActionStripWrapper
            isWebView={isWebView}
            handleBackPress={handleBackPress}
          >
            {showNoLinkedAccount && (
              <CardComponent innerHeight={height}>
                <StyledLinkImage src={LinkBadge} />

                <StyledHeading>Where do we send your money?</StyledHeading>
                <StyledBody>
                  {isDowngradeFlow
                    ? 'Before we can change your subscription,'
                    : 'Before we can close your account,'}{' '}
                  you'll need to link a checking account to transfer your money
                  into
                </StyledBody>
                <StyledCloseAccountButton onPress={handleLinkAccount}>
                  Link checking account
                </StyledCloseAccountButton>
              </CardComponent>
            )}

            {showPendingTransfers && (
              <CardComponent innerHeight={height}>
                <CenteredContainer>
                  <StyledImage src={ErrorIcon} />

                  <StyledHeading>You have money transferring</StyledHeading>
                  {showRecurringTransfersOn && (
                    <StyledBody>
                      {isDowngradeFlow
                        ? 'Before we can change your subscription,'
                        : 'Before we can close your account,'}{' '}
                      you'll need to wait for any current transfers to finish.
                      In the meantime, we recommend cancelling your recurring
                      transfers.
                    </StyledBody>
                  )}
                  {!showRecurringTransfersOn && (
                    <StyledBody>
                      {isDowngradeFlow
                        ? 'Before we can change your subscription,'
                        : 'Before we can close your account,'}{' '}
                      you'll need to wait for any current transfers to finish.
                    </StyledBody>
                  )}
                </CenteredContainer>
                {showRecurringTransfersOn && (
                  <RedStyledCloseAccountButton
                    onPress={handleRecurringTransferCancellation}
                  >
                    Cancel recurring transfers
                  </RedStyledCloseAccountButton>
                )}
                {!showRecurringTransfersOn && (
                  <StyledCloseAccountButton onPress={handleViewHistory}>
                    Ok, got it
                  </StyledCloseAccountButton>
                )}
              </CardComponent>
            )}

            {showNegativeBalance && (
              <CardComponent innerHeight={height}>
                <CenteredContainer>
                  <StyledImage src={ErrorIcon} />

                  <StyledHeading>You have a negative balance</StyledHeading>
                  <StyledBody>
                    Your {eligibilityStatus.account} account has a negative
                    balance. Please contact our support team and we'll help
                    close your account.
                  </StyledBody>
                </CenteredContainer>
                <RedStyledCloseAccountButton onPress={handleContactSupport}>
                  Contact support
                </RedStyledCloseAccountButton>
              </CardComponent>
            )}
          </CloseAccountEdgeCaseActionStripWrapper>
        </FullscreenTakeover>
      )}
      {!loading && !isWebView && (
        <CloseAccountEdgeCaseActionStripWrapper
          isWebView={isWebView}
          handleBackPress={handleBackPress}
        >
          {showNoLinkedAccount && (
            <CardComponent>
              <StyledLinkImage src={LinkBadge} />

              <StyledHeading>Where do we send your money?</StyledHeading>
              <StyledBody>
                {isDowngradeFlow
                  ? 'Before we can change your subscription,'
                  : 'Before we can close your account,'}{' '}
                you'll need to link a checking account to transfer your money
                into.
              </StyledBody>
              <StyledCloseAccountButton onPress={handleLinkAccount}>
                Link checking account
              </StyledCloseAccountButton>
            </CardComponent>
          )}

          {showPendingTransfers && (
            <CardComponent>
              <StyledImage src={ErrorIcon} />

              <StyledHeading>You have money transferring</StyledHeading>
              {showRecurringTransfersOn && (
                <StyledBody>
                  {isDowngradeFlow
                    ? 'Before we can change your subscription,'
                    : 'Before we can close your account,'}{' '}
                  you'll need to wait for any current transfers to finish. In
                  the meantime, we recommend cancelling your recurring
                  transfers.
                </StyledBody>
              )}
              {!showRecurringTransfersOn && (
                <StyledBody>
                  {isDowngradeFlow
                    ? 'Before we can change your subscription,'
                    : 'Before we can close your account,'}{' '}
                  you'll need to wait for any current transfers to finish.
                </StyledBody>
              )}
              {showRecurringTransfersOn && (
                <RedStyledCloseAccountButton
                  onPress={handleRecurringTransferCancellation}
                >
                  Cancel recurring transfers
                </RedStyledCloseAccountButton>
              )}
              {!showRecurringTransfersOn && (
                <StyledCloseAccountButton onPress={handleViewHistory}>
                  Ok, got it
                </StyledCloseAccountButton>
              )}
            </CardComponent>
          )}

          {showNegativeBalance && (
            <CardComponent>
              <StyledImage src={ErrorIcon} />

              <StyledHeading>You have a negative balance</StyledHeading>
              <StyledBody>
                Your {eligibilityStatus.account} account has a negative balance.
                Please contact our support team and we'll help close your
                account.
              </StyledBody>
              <RedStyledCloseAccountButton onPress={handleContactSupport}>
                Contact support
              </RedStyledCloseAccountButton>
            </CardComponent>
          )}
        </CloseAccountEdgeCaseActionStripWrapper>
      )}
      {showRecurringTransferModal && (
        <ConfirmationModal
          image={CheckBadge}
          body="We've gone ahead and canceled all your recurring transfers. Any transfers that are currently processing will still go through though."
          header="Recurring transfers canceled"
          isOpen={showRecurringTransferModal}
          actions={[
            {
              label: 'Ok',
              type: ModalActionTypes.primary,
              handler: handleCloseRecurringModal,
            },
          ]}
        />
      )}
    </>
  )
}

export default Page
